import { FC, ReactNode, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import dayjs from "dayjs";
import { WarningOutlined } from "@ant-design/icons";
import { AlertTriangle, TrendingUp } from "lucide-react";
import {
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  formatNumber,
  toArabicIndicDigitsInString,
  wrapBidiIsolate,
} from "src/utils/numberFormat";
import { getDashboardKpiLucideIconComponent } from "src/utils/dashboardKpiLucideIcon";
import {
  type CLTVKPICard,
  type CustomerAnalyticsRow,
  type PredictiveInsightItem,
} from "src/services/CLTVDashboardAPI";
import {
  cltvSegmentNameSlug,
  type CltvRiskTierCode,
} from "./CltvDefinitions";
import { CHART_COLOR_PALETTE } from "src/components/charts/ChartPalette";

export type EvolutionSeriesKey =
  | "loyal"
  | "highSpender"
  | "likelyToChurn"
  | "idle"
  | "oneTimer"
  | "indecisive"
  | "cartAbandoners"
  | "unsubscribed";

export type InsightDisplay = {
  key: string;
  title: string;
  subtitle: string;
  value: string | number;
  badge: string;
  progress: number;
  tone: "positive" | "warning" | "info";
  raw: PredictiveInsightItem;
};

/** Swaps Western digits for Eastern Arabic-Indic ones, keeping the surrounding punctuation. */
export const toLocaleDigits = (value: string, isArabic: boolean) =>
  isArabic ? wrapBidiIsolate(toArabicIndicDigitsInString(value)) : value;

/**
 * KPI label with the curated translation first and the API's own string as the fallback.
 *
 * `t(key, { defaultValue: apiValue })` cannot be used directly: when `apiValue` is `undefined`
 * i18next has no default to return and hands back the *key itself*, which is truthy — so a card
 * whose id has no `cltv.kpi.*` entry rendered `cltv.kpi.<id>.badge` as its badge text. Passing an
 * empty `defaultValue` keeps a missing key falsy, so the API value (or nothing) is used instead.
 */
export const resolveKpiLabel = (t: TFunction, key: string, apiValue: string | undefined): string =>
  t(key, { defaultValue: "" }) || apiValue?.trim() || "";

export const RISK_TIER_ORDER: Record<CltvRiskTierCode, number> = { high: 0, medium: 1, low: 2 };

export const RISK_TIER_COLORS: Record<CltvRiskTierCode, string> = {
  high: "#e05252",
  medium: "#f6a623",
  low: "#4caf50",
};

export const RISK_TIER_FALLBACK_COLOR = "#8d4fd3";

export const insightToneColor = (tone: InsightDisplay["tone"]) => {
  if (tone === "positive") {
    return "#94d0b5";
  }
  if (tone === "warning") {
    return "#f6c15f";
  }
  return "#8d4fd3";
};

export type CustomerSegmentTone =
  | "loyal"
  | "high-spender"
  | "likely-to-churn"
  | "one-timer"
  | "idle"
  | "indecisive"
  | "cart-abandoner"
  | "unsubscribed";

// Only the fields the backend accepts for `sort_by`. Segments and predicted
// CLTV are not sortable server-side (they'd return 422).
export type CustomerTableSortKey =
  | "customerName"
  | "email"
  | "profile"
  | "totalSpend"
  | "totalOrders"
  | "predictedSpend"
  | "predictedOrders";

export const SORT_KEY_TO_API_FIELD: Record<CustomerTableSortKey, string> = {
  customerName: "customer_name",
  email: "email",
  profile: "customer_age_months",
  totalSpend: "total_spend",
  totalOrders: "total_orders",
  predictedSpend: "predicted_spend_3m",
  predictedOrders: "predicted_orders_3m",
};

export const SEGMENT_PILL_FILTERS: Array<{ key: string; labelKey: string }> = [
  { key: "loyal", labelKey: "cltv.segmentNames.loyal" },
  { key: "high_spender", labelKey: "cltv.segmentNames.high_spender" },
  { key: "likely_to_churn", labelKey: "cltv.segmentNames.likely_to_churn" },
  { key: "idle", labelKey: "cltv.segmentNames.idle" },
  { key: "one_timer", labelKey: "cltv.segmentNames.one_timer" },
  { key: "cart_abandoner", labelKey: "cltv.segmentNames.cart_abandoner" },
  { key: "indecisive", labelKey: "cltv.segmentNames.indecisive" },
  { key: "unsubscribed", labelKey: "cltv.segmentNames.unsubscribed" },
];

export const CltvSectionTitle: FC<{
  title: string;
  tooltip?: DefinitionTooltipInfo;
}> = ({ title, tooltip }) => {
  const { t } = useTranslation();
  return (
    <div className="cltv-dashboard__title-with-tooltip">
      <h3>{title}</h3>
      {tooltip ? (
        <DefinitionTooltip
          label={title}
          info={tooltip}
          formulaTitle={t("cltv.common.formula")}
        />
      ) : null}
    </div>
  );
};

export const SpendingTooltip: FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{
    name?: string;
    dataKey?: string;
    value: number;
    payload?: {
      fullLabel?: string;
      status?: string;
      predictedRaw?: number | null;
      actualRaw?: number | null;
      predictedFormattedValue?: string;
      actualFormattedValue?: string;
    };
  }>;
  formatBucket?: (_raw: string) => string;
  formatMoney: (_value: number) => string;
}> = ({ active, label, payload, formatBucket, formatMoney }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const labelText =
    label !== undefined && label !== null && formatBucket
      ? formatBucket(String(label))
      : label;

  const point = payload[0]?.payload;
  const status = point?.status ?? "forecast";
  const statusLabel = t(`cltv.spendingStatus.${status}`, {
    defaultValue: t("cltv.spendingStatus.forecast"),
  });
  const rows = [
    {
      key: "predicted",
      label: t("cltv.spendingTooltip.predicted"),
      value: point?.predictedRaw ?? null,
      formattedValue: point?.predictedFormattedValue,
    },
    {
      key: "actual",
      label: t("cltv.spendingTooltip.actual"),
      value: point?.actualRaw ?? null,
      formattedValue: point?.actualFormattedValue,
    },
  ].filter(row => row.value !== null && row.value !== undefined);

  return (
    <div className="analytics-chart-tooltip">
      <div className="analytics-chart-tooltip__bar" />
      <div className="analytics-chart-tooltip__content">
        <div className="analytics-chart-tooltip__header">
          <strong>{point?.fullLabel ?? labelText}</strong>
          <span className="analytics-chart-tooltip__pill">{statusLabel}</span>
        </div>
        {rows.map(row => (
          <span key={row.key} className="analytics-chart-tooltip__value">
            <span className="analytics-chart-tooltip__name">
              {row.label}
            </span>{" "}
            {row.formattedValue ? normalizeCurrencyLabel(row.formattedValue) : formatMoney(row.value ?? 0)}
          </span>
        ))}
      </div>
    </div>
  );
};

export const EvolutionTooltip: FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{ name: string; value: number; color?: string }>;
  formatDate: (_value: string) => string;
}> = ({
  active,
  label,
  payload,
  formatDate,
}) => {
    const { t, i18n } = useTranslation();
    if (!active || !payload?.length) {
      return null;
    }

    const seriesLabel = (name: string) =>
      t(`cltv.segments.${name}`, {
        defaultValue: name.replace(/([a-z])([A-Z])/g, "$1 $2"),
      });

    const sortedPayload = [...payload]
      .filter(item => Number(item.value) > 0)
      .sort((a, b) => Number(b.value) - Number(a.value));

    return (
      <div className="analytics-chart-tooltip">
        <div className="analytics-chart-tooltip__bar" />
        <div className="analytics-chart-tooltip__content">
          <span className="analytics-chart-tooltip__label">
            {formatDate(String(label ?? ""))}
          </span>
          {sortedPayload.map(item => (
            <span key={item.name} className="analytics-chart-tooltip__value">
              <span className="analytics-chart-tooltip__row-label">
                <i style={{ backgroundColor: item.color ?? "#98a2b3" }} />
                <span className="analytics-chart-tooltip__name">{seriesLabel(item.name)}:</span>
              </span>{" "}
              {formatNumber(item.value, {}, i18n.language)}
            </span>
          ))}
        </div>
      </div>
    );
  };

const FLOATING_TOOLTIP_MARGIN = 8;
const FLOATING_TOOLTIP_GAP = 12;

const clampValue = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(max, min));

/**
 * Positions one axis beside `point` — after it (right / below) by default, flipped
 * before it when that overflows, else whichever side has more room — then clamps
 * the result into `[minEdge, maxEdge]`.
 */
const placeAlongAxis = (
  point: number,
  boxSize: number,
  minEdge: number,
  maxEdge: number
) => {
  const afterStart = point + FLOATING_TOOLTIP_GAP;
  const beforeStart = point - boxSize - FLOATING_TOOLTIP_GAP;
  const fitsAfter = afterStart + boxSize <= maxEdge - FLOATING_TOOLTIP_MARGIN;
  const fitsBefore = beforeStart >= minEdge + FLOATING_TOOLTIP_MARGIN;

  let start = afterStart;
  if (!fitsAfter) {
    const roomierSideStart =
      point - minEdge > maxEdge - point ? beforeStart : afterStart;
    start = fitsBefore ? beforeStart : roomierSideStart;
  }

  return clampValue(
    start,
    minEdge + FLOATING_TOOLTIP_MARGIN,
    maxEdge - boxSize - FLOATING_TOOLTIP_MARGIN
  );
};

export const FloatingTooltip: FC<{
  x: number;
  y: number;
  contentKey: string | number;
  children: ReactNode;
}> = ({ x, y, contentKey, children }) => {
  const boxRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) {
      return;
    }

    // `x`/`y` are the native mouse event's `clientX`/`clientY` (see the chart's
    // `onMouseMove` handlers) — already real viewport pixels, so no conversion from
    // a chart-local coordinate is needed here at all. An earlier version derived the
    // point from recharts' own `activeCoordinate` plus the chart container's
    // `getBoundingClientRect()`, which is one more place for the two coordinate
    // spaces to drift apart; going straight off the cursor sidesteps that entirely.
    // The wrapper under-reports its size: `width: max-content` around a flex card whose
    // `overflow: hidden` collapses that intrinsic width. Measure the card that paints.
    const contentRect = (box.firstElementChild ?? box).getBoundingClientRect();
    const boxWidth = Math.max(box.offsetWidth, Math.ceil(contentRect.width));
    const boxHeight = Math.max(box.offsetHeight, Math.ceil(contentRect.height));
    // Unlike `window.inner*`, these exclude the scrollbar gutters.
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const pointX = x;
    const pointY = y;

    // The sidebar owns the viewport's leading edge, so clamping to 0 leaves the box
    // under it (as in InstagramFollowersReport's `getCursorTooltipPosition`). The
    // `.ant-layout-sider` part skips the mobile drawer, which reuses `.sidebar`.
    let viewportLeftEdge = 0;
    let viewportRightEdge = viewportWidth;
    const sidebarRect = document
      .querySelector(".sidebar.ant-layout-sider")
      ?.getBoundingClientRect();
    if (sidebarRect && sidebarRect.width > 0) {
      if (sidebarRect.left <= 1) {
        viewportLeftEdge = sidebarRect.right;
      } else if (sidebarRect.right >= viewportWidth - 1) {
        viewportRightEdge = sidebarRect.left;
      }
    }

    const left = placeAlongAxis(pointX, boxWidth, viewportLeftEdge, viewportRightEdge);
    const top = placeAlongAxis(pointY, boxHeight, 0, viewportHeight);

    box.style.setProperty("left", `${left}px`);
    box.style.setProperty("top", `${top}px`);
    box.classList.add("cltv-dashboard__floating-tooltip--placed");

    return () => {
      box.classList.remove("cltv-dashboard__floating-tooltip--placed");
    };
  }, [x, y, contentKey]);

  // Portalled like DefinitionTooltip: `position: fixed` stops being viewport-relative
  // once any ancestor gains a `transform`/`filter`/`will-change`.
  return createPortal(
    <div
      ref={boxRef}
      // `cltv-dashboard` keeps the scoped `.analytics-chart-tooltip` styles matching.
      className="cltv-dashboard cltv-dashboard__floating-tooltip"
    >
      {children}
    </div>,
    document.body,
  );
};

export const insightIcon = (tone: InsightDisplay["tone"]) => {
  if (tone === "warning") {
    return <WarningOutlined />;
  }
  return <TrendingUp size={16} />;
};

export const normalizeCurrencyLabel = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

/** Pull the currency code/symbol off the front of a backend-formatted value, e.g. "USD 92.98" -> "USD". */
export const extractCurrencyCode = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  const codeMatch = str.match(/^([A-Za-z]{2,5})\s/);
  if (codeMatch) {
    return codeMatch[1].toUpperCase();
  }
  const symbolMatch = str.match(/^(\$|Rs\.?|₨|₦|€|£)/);
  return symbolMatch ? symbolMatch[1] : null;
};

/** Prefer the backend's formattedValue; fall back to client-side formatting of the raw number. */
export const formatMoneyCell = (
  formattedValue: string | null | undefined,
  raw: number | null | undefined,
  formatFallback: (_value: number) => string,
): string | null => {
  if (formattedValue) {
    return normalizeCurrencyLabel(formattedValue);
  }
  if (raw === null || raw === undefined) {
    return null;
  }
  return formatFallback(raw);
};

export const escapeCsvCell = (value: string | number | null | undefined) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, "\"\"")}"` : text;
};

export const isChurnKpiCard = (card: CLTVKPICard) =>
  card.id === "churn-risk" || card.id === "churn_risk" || /churn/i.test(card.id);

export const isAverageMonthlySpendKpiCard = (card: CLTVKPICard) =>
  card.id === "average_monthly_spend"
  || card.id === "average-monthly-spend"
  || /average.*monthly.*spend/i.test(card.id)
  || /average.*monthly.*spend/i.test(card.title);

export const getKpiIcon = (card: CLTVKPICard) => {
  const normalized = `${card.id} ${card.title}`.toLowerCase();

  if (normalized.includes("churn")) {
    return <AlertTriangle size={22} />;
  }

  const Icon = getDashboardKpiLucideIconComponent(`${card.id} ${card.title}`);
  return <Icon size={22} />;
};

export const progressFromPercentValue = (value: string | number) => {
  const m = String(value).match(/([-+]?\d+\.?\d*)/);
  if (!m) {
    return 72;
  }
  const n = Math.abs(parseFloat(m[1]));
  if (!Number.isFinite(n)) {
    return 72;
  }
  return Math.min(100, Math.max(8, n));
};

/** Customer age bucketed to whole years, falling back to months below one year. */
export const getCustomerLifetimeLabel = (monthsActive: number, t: TFunction, language: string) => {
  const wholeYears = Math.min(4, Math.floor(monthsActive / 12));
  if (wholeYears >= 1) {
    return t(wholeYears === 1 ? "cltv.table.lifetimeYear" : "cltv.table.lifetimeYears", {
      value: formatNumber(wholeYears, {}, language),
    });
  }
  return t("cltv.table.lifetimeMonths", {
    value: formatNumber(Math.max(1, monthsActive), {}, language),
  });
};

export const getTotalOrders = (row: CustomerAnalyticsRow) =>
  row.totalOrdersSince2023
  ?? ((row.orders_0_30d ?? 0) + (row.orders_30_60d ?? 0) + (row.orders_60_90d ?? 0));

export const getTotalSpend = (row: CustomerAnalyticsRow) =>
  row.totalSpendSince2023
  ?? ((row.spend_0_30d ?? 0) + (row.spend_30_60d ?? 0) + (row.spend_60_90d ?? 0));

export const getPredictedOrders = (row: CustomerAnalyticsRow) =>
  row.predictedOrders3m
  ?? ((row.predOrders_0_30d ?? 0) + (row.predOrders_30_60d ?? 0));

export const getPredictedSpend = (row: CustomerAnalyticsRow) =>
  row.predictedSpend3m
  ?? ((row.predSpend_0_30d ?? 0) + (row.predSpend_30_60d ?? 0));

export const getPredictedCltv = (row: CustomerAnalyticsRow) =>
  row.predictedLtv90d ?? (getTotalSpend(row) + getPredictedSpend(row));

/** `cltv.segments.*` leaf that renders each tone, so the pills reuse the segment-name copy. */
export const CUSTOMER_SEGMENT_TONE_I18N_LEAF: Record<CustomerSegmentTone, string> = {
  loyal: "loyal",
  "high-spender": "highSpender",
  "likely-to-churn": "likelyToChurn",
  "one-timer": "oneTimer",
  idle: "idle",
  indecisive: "indecisive",
  "cart-abandoner": "cartAbandoners",
  unsubscribed: "unsubscribed",
};

export const CUSTOMER_SEGMENT_TONES = Object.keys(
  CUSTOMER_SEGMENT_TONE_I18N_LEAF
) as CustomerSegmentTone[];

export const SEGMENT_SLUG_TO_TONE: Record<string, CustomerSegmentTone> = {
  loyal: "loyal",
  high_spender: "high-spender",
  high_spenders: "high-spender",
  likely_to_churn: "likely-to-churn",
  one_timer: "one-timer",
  one_timers: "one-timer",
  idle: "idle",
  cart_abandoner: "cart-abandoner",
  cart_abandoners: "cart-abandoner",
  unsubscribed: "unsubscribed",
  indecisive: "indecisive",
  indecisive_buyer: "indecisive",
  indecisive_buyers: "indecisive",
};

/**
 * Resolves a segment value returned by the API to its tone. The API localizes
 * `customer-analytics[].segments`, so the English slug table alone matches nothing under
 * `lang=ar` and every Arabic row would silently fall through to the heuristic below —
 * showing different segments than the same row shows in English. Localized labels are
 * therefore matched too, via the `cltv.segments.*` copy this dashboard renders for each tone.
 *
 * ⚠️ That second pass does NOT currently rescue Arabic: the backend translates these labels from
 * its own `locales/ar.json`, and its wording differs from ours for 7 of the 8 segments (only
 * `unsubscribed` happens to match) — verified against the segment labels quoted in the backend's
 * "CLTV & Organic Media — Arabic formatting" reference. Arabic rows still reach the heuristic, so
 * their pills can disagree with the English ones. Matching more strings would only couple us to
 * another service's copy.
 *
 * TODO(backend): `customer-analytics[].segments` should expose the stable English ids (the same
 * ones the `segment` filter param accepts) — as a `{id, label}` pair, which the backend has
 * already offered for `customer-analytics/segments`. That is the only fix that closes this, and it
 * would also let `fetchCLTVKpis` drop its companion `lang=en` request for segment colors.
 */
export const buildSegmentToneResolver = (t: TFunction) => {
  const localizedToTone = new Map<string, CustomerSegmentTone>();
  for (const tone of CUSTOMER_SEGMENT_TONES) {
    const label = t(`cltv.segments.${CUSTOMER_SEGMENT_TONE_I18N_LEAF[tone]}`, {
      defaultValue: "",
    });
    if (label) {
      localizedToTone.set(label.trim().toLowerCase(), tone);
    }
  }

  return (segment: string): CustomerSegmentTone | null =>
    SEGMENT_SLUG_TO_TONE[cltvSegmentNameSlug(segment)] ??
    localizedToTone.get(String(segment ?? "").trim().toLowerCase()) ??
    null;
};

export const getCustomerSegmentTone = (
  row: CustomerAnalyticsRow,
  normalizeCustomerSegmentTone: (_segment: string) => CustomerSegmentTone | null
): CustomerSegmentTone[] => {
  const totalOrders = getTotalOrders(row);
  const totalSpend = getTotalSpend(row);
  const predictedCltv = getPredictedCltv(row);
  const backendSegments = (Array.isArray(row.segment) ? row.segment : row.segment ? [row.segment] : [])
    .map(normalizeCustomerSegmentTone)
    .filter((segment): segment is CustomerSegmentTone => Boolean(segment));

  if (backendSegments.length > 0) {
    return Array.from(new Set(backendSegments)).slice(0, 3);
  }

  const tones: CustomerSegmentTone[] = [];

  if ((row.segmentLoyal ?? 0) > 0 || totalOrders >= 5) {
    tones.push("loyal");
  }

  if ((row.segmentHighSpenders ?? 0) > 0 || totalSpend >= 1500 || predictedCltv >= 1500) {
    tones.push("high-spender");
  }

  if ((row.segmentOneTimers ?? 0) > 0 || (totalOrders <= 1 && totalSpend > 0)) {
    tones.push("one-timer");
  }

  if ((row.segmentLikelyToChurn ?? 0) > 0 || row.likelyToChurn || totalOrders === 0 && predictedCltv > 0) {
    tones.push("likely-to-churn");
  }

  if ((row.segmentCartAbandoners ?? 0) > 0) {
    tones.push("cart-abandoner");
  }

  if ((row.segmentUnsubscribed ?? 0) > 0) {
    tones.push("unsubscribed");
  }

  if ((row.segmentIndecisive ?? 0) > 0) {
    tones.push("indecisive");
  }

  if ((row.segmentIdle ?? 0) > 0 || (totalOrders === 0 && totalSpend === 0 && predictedCltv === 0)) {
    tones.push("idle");
  }

  if (tones.length === 0) {
    tones.push("indecisive");
  }

  return tones.slice(0, 3);
};

export const getCustomerSegmentLabel = (tone: CustomerSegmentTone, t: TFunction) =>
  t(`cltv.segments.${CUSTOMER_SEGMENT_TONE_I18N_LEAF[tone]}`);


export const SEGMENT_EVOLUTION_SERIES: Array<{ key: EvolutionSeriesKey; labelKey: string; color: string }> = [
  { key: "loyal", labelKey: "cltv.segments.loyal", color: CHART_COLOR_PALETTE[0] },
  { key: "highSpender", labelKey: "cltv.segments.highSpender", color: CHART_COLOR_PALETTE[1] },
  { key: "likelyToChurn", labelKey: "cltv.segments.likelyToChurn", color: CHART_COLOR_PALETTE[2] },
  { key: "idle", labelKey: "cltv.segments.idle", color: CHART_COLOR_PALETTE[3] },
  { key: "oneTimer", labelKey: "cltv.segments.oneTimer", color: CHART_COLOR_PALETTE[4] },
  { key: "indecisive", labelKey: "cltv.segments.indecisive", color: CHART_COLOR_PALETTE[5] },
  { key: "cartAbandoners", labelKey: "cltv.segments.cartAbandoners", color: CHART_COLOR_PALETTE[6] },
  { key: "unsubscribed", labelKey: "cltv.segments.unsubscribed", color: CHART_COLOR_PALETTE[7] },
];

export const mapPredictiveInsight = (insight: PredictiveInsightItem): InsightDisplay => {
  let tone: InsightDisplay["tone"] = "info";
  if (insight.id === "churn-risk-alert" || insight.id === "churn-alert") {
    tone = "warning";
  } else if (insight.id === "revenue-growth") {
    tone = "positive";
  }

  let progress = 70;
  if (insight.id === "revenue-growth") {
    progress = progressFromPercentValue(insight.value ?? 0);
  } else if (insight.id === "churn-risk-alert" || insight.id === "churn-alert") {
    progress = 64;
  } else if (insight.id === "upsell-opportunities") {
    progress = 74;
  }

  return {
    key: insight.id,
    title: insight.title,
    subtitle: insight.description ?? "",
    value: insight.value ?? "",
    badge: insight.badge ?? "",
    progress,
    tone,
    raw: insight,
  };
};

export const CLTV_PDF_TABLE_MAX_ROWS = 500;
export const CLTV_PDF_TABLE_PAGE_SIZE = 100;

export const getDefaultMonthRange = () => {
  const now = dayjs();
  const end = now.startOf("month").format("YYYY-MM-DD");
  const start = now.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
  return { start, end };
};
